import { isSquadDynamicCell, type SquadDynamicCell } from './squadFondsdatenDynamic';
import { filterSquadKvRows, type SquadKvRow } from './squadKvRows';

/**
 * Gebühren (page 2) from the fund's data (S1, 09.10.2026): issue surcharge, redemption fee
 * and ongoing charges with their date. The management and performance fee have no source
 * in the delivery and stay typed.
 */
export const SQUAD_DYNAMIC_GEBUEHREN_KEYS = ['ausgabeaufschlag', 'ruecknahmeabschlag', 'ter'] as const;
export type SquadDynamicGebuehrKey = (typeof SQUAD_DYNAMIC_GEBUEHREN_KEYS)[number];

export function isSquadDynamicGebuehrKey(key: string): key is SquadDynamicGebuehrKey {
  return (SQUAD_DYNAMIC_GEBUEHREN_KEYS as readonly string[]).includes(key);
}

const stammdaten = (field: string, valueType: 'string' | 'date' = 'string') => ({
  type: 'table' as const,
  table: 'cixon_stammdaten_extension',
  field,
  valueType,
});

/**
 * Each table field is fetched on its own and as delivered (fractions as text, the TER date
 * formatted by the server); the wording is put together in formatSquadGebuehr.
 */
export function makeSquadGebuehrDynamic(key: SquadDynamicGebuehrKey): SquadDynamicCell {
  switch (key) {
    case 'ausgabeaufschlag':
      return { isDynamic: true, fields: [stammdaten('ausgabeaufschlag')] };
    case 'ruecknahmeabschlag':
      return { isDynamic: true, fields: [stammdaten('ruecknahmeabschlag')] };
    case 'ter':
      return { isDynamic: true, fields: [stammdaten('ter'), stammdaten('terDate', 'date')] };
    default: {
      const unhandled: never = key;
      throw new Error(`No dynamic default for Gebühren key ${String(unhandled)}`);
    }
  }
}

/**
 * The values every SQUAD document carried until 09.10.2026 (mapper default). The TER date
 * in it was a year old by then. Only these exact texts are replaced; anything typed stays.
 */
export const SQUAD_LEGACY_GEBUEHREN_DEFAULTS: Record<SquadDynamicGebuehrKey, string> = {
  ausgabeaufschlag: 'aktuell bis zu 5,00 %',
  ruecknahmeabschlag: 'keiner',
  ter: '1,98 % p.a. Stand: 31.12.2024',
};

/**
 * Gebühren on load: missing key → dynamic, the old fixed default → dynamic. An empty string
 * is what the Dynamic switch writes when a row is turned manual, so it stays.
 */
export function ensureSquadDynamicGebuehren(weitereDaten: Record<string, unknown> | undefined | null) {
  const next: Record<string, unknown> = { ...(weitereDaten || {}) };
  for (const key of SQUAD_DYNAMIC_GEBUEHREN_KEYS) {
    const current = next[key];
    if (isSquadDynamicCell(current)) continue;
    if (current == null || String(current).trim() === SQUAD_LEGACY_GEBUEHREN_DEFAULTS[key]) {
      next[key] = makeSquadGebuehrDynamic(key);
    }
  }
  return next;
}

const percent = (n: number) =>
  `${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`;

const toNumber = (raw: string | undefined) => {
  const text = String(raw ?? '').trim();
  if (!text) return null;
  const n = Number(text.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

/**
 * Wording as in the CIXON master: "aktuell bis zu 5,00 %", "keiner",
 * "1,98 % p.a. Stand: 31.12.2025". `raws` holds the fetched table fields in cell order.
 * Returns '' when the data has no value — the row is then left out and reported.
 */
export function formatSquadGebuehr(key: SquadDynamicGebuehrKey, raws: Array<string | undefined>): string {
  const n = toNumber(raws[0]);
  if (n == null) return '';
  if (key === 'ausgabeaufschlag') return n === 0 ? 'keiner' : `aktuell bis zu ${percent(n)}`;
  if (key === 'ruecknahmeabschlag') return n === 0 ? 'keiner' : percent(n);
  const asOf = String(raws[1] ?? '').trim();
  return asOf ? `${percent(n)} p.a. Stand: ${asOf}` : `${percent(n)} p.a.`;
}

/** Gebühren rows for preview/PDF — a dynamic row shows its fetched text or is left out. */
export function resolveSquadGebuehren(
  weitereDaten: Record<string, unknown> | undefined | null,
  cache?: Map<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  Object.entries(weitereDaten || {}).forEach(([key, value]) => {
    out[key] = isSquadDynamicCell(value) ? cache?.get(key) || '' : String(value ?? '');
  });
  return out;
}

export function resolveSquadGebuehrenRows(
  weitereDaten: Record<string, unknown> | undefined | null,
  keys: Array<{ key: string; label: string }>,
  cache?: Map<string, string>,
): SquadKvRow[] {
  const resolved = resolveSquadGebuehren(weitereDaten, cache);
  return filterSquadKvRows(keys.map(({ key, label }) => ({ label, value: resolved[key] ?? '' })));
}
